import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Key,
  Copy,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Zap,
  Upload,
  Search,
  Clock,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  FileText,
  Lightbulb,
  Send,
  Sparkles,
} from 'lucide-react';
import { zapierApiKeyService, ZapierApiKey } from '../../services/zapierApiKeyService';
import { lambdaEndpoints } from '../../aws-config';
import { TopNav } from '../ui/top-nav';

export const IntegrationsPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ZapierApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(true);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  // Integration request form state
  const [integrationRequest, setIntegrationRequest] = useState({
    platformName: '',
    useCase: '',
  });
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const keys = await zapierApiKeyService.listKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
      // Don't show error toast on initial load - API might not be deployed yet
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setIsCreating(true);
    try {
      const response = await zapierApiKeyService.createKey(newKeyName.trim());
      // The response contains the key fields directly (key_id, name, created_at, etc.)
      // along with api_key (the actual key value shown once)
      const newKey: ZapierApiKey = {
        key_id: response.key_id,
        name: response.name,
        created_at: response.created_at,
        is_active: true,
      };
      setApiKeys([newKey, ...apiKeys]);
      setNewlyCreatedKey(response.api_key);
      setNewKeyName('');
      setShowCreateForm(false);
      toast.success('API key created successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    setRevokingKeyId(keyId);
    try {
      await zapierApiKeyService.revokeKey(keyId);
      setApiKeys(apiKeys.filter((k) => k.key_id !== keyId));
      if (newlyCreatedKey) {
        setNewlyCreatedKey(null);
      }
      toast.success('API key revoked successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to revoke API key');
    } finally {
      setRevokingKeyId(null);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleIntegrationRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationRequest.platformName.trim()) {
      toast.error('Please enter a platform name');
      return;
    }

    setIsSubmittingRequest(true);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(lambdaEndpoints.integrationRequest, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platformName: integrationRequest.platformName,
          useCase: integrationRequest.useCase,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to submit request');
      }

      setRequestSubmitted(true);
      toast.success('Integration request submitted! We\'ll review it and get back to you.');
      setIntegrationRequest({ platformName: '', useCase: '' });
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6 space-y-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold text-foreground">Integrations</h1>
            <p className="text-sm text-muted-foreground">
              Connect BigSplice to Zapier and automate your data workflows
            </p>
          </div>
        </div>

        {/* Popular Integrations via Zapier */}
        <div className="py-5 px-6 mx-4 rounded-xl bg-muted/30 dark:bg-muted/10 border border-border/30">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Connect to 7,000+ apps via Zapier</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {/* Google Sheets - document with table */}
            <div className="w-6 h-6" title="Google Sheets">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 11V9h-4V5h-2v4H9V5H7v4H3v2h4v4H3v2h4v4h2v-4h4v4h2v-4h4v-2h-4v-4h4zm-6 4H9v-4h4v4z" fill="#0F9D58"/>
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" fill="#0F9D58"/>
              </svg>
            </div>
            {/* Slack */}
            <div className="w-6 h-6 " title="Slack">
              <svg viewBox="0 0 24 24" fill="#4A154B"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/></svg>
            </div>
            {/* Gmail */}
            <div className="w-6 h-6 " title="Gmail">
              <svg viewBox="0 0 24 24" fill="#EA4335"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/></svg>
            </div>
            {/* Salesforce */}
            <div className="w-6 h-6 " title="Salesforce">
              <svg viewBox="0 0 24 24" fill="#00A1E0"><path d="M10.006 5.415a4.195 4.195 0 0 1 3.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.16 5.22c-.435 0-.855-.045-1.26-.135-.63 1.5-2.1 2.55-3.84 2.55a4.028 4.028 0 0 1-1.98-.51 4.633 4.633 0 0 1-3.72 1.875c-2.175 0-4.005-1.5-4.53-3.525A4.163 4.163 0 0 1 0 12.165c0-2.295 1.845-4.17 4.125-4.17.345 0 .69.045 1.02.12a4.7 4.7 0 0 1 4.86-2.7z"/></svg>
            </div>
            {/* HubSpot */}
            <div className="w-6 h-6 " title="HubSpot">
              <svg viewBox="0 0 24 24" fill="#FF7A59"><path d="M18.164 7.93V5.084a2.198 2.198 0 0 0 1.267-1.984v-.066A2.198 2.198 0 0 0 17.233.836h-.066a2.198 2.198 0 0 0-2.198 2.198v.066c0 .868.503 1.618 1.233 1.974v2.862a5.91 5.91 0 0 0-2.462 1.212l-6.555-5.1a2.635 2.635 0 1 0-1.4 1.803l6.39 4.972a5.923 5.923 0 0 0 .017 5.918l-1.9 1.9a2.07 2.07 0 0 0-.614-.094 2.105 2.105 0 1 0 2.105 2.105c0-.217-.034-.426-.094-.623l1.9-1.9a5.926 5.926 0 1 0 4.411-10.199z"/></svg>
            </div>
            {/* Mailchimp */}
            <div className="w-6 h-6 " title="Mailchimp">
              <svg viewBox="0 0 24 24" fill="#FFE01B"><path d="M19.469 14.627c-.103-.21-.218-.418-.344-.62a5.236 5.236 0 0 0-.756-.87c-.08-.074-.163-.144-.25-.211-.066-.05-.115-.115-.19-.156a4.76 4.76 0 0 0-2.327-.767 7.19 7.19 0 0 0-.16-.01 5.066 5.066 0 0 0-.665.016c-1.428.11-2.608.86-3.254 1.79a2.63 2.63 0 0 0-.124.199 2.738 2.738 0 0 0-.203.408c-.08.19-.14.388-.18.59a3.058 3.058 0 0 0 .063 1.49 3.3 3.3 0 0 0 1.64 2.032c.458.254.969.41 1.5.458a4.41 4.41 0 0 0 .54.023c.47-.01.93-.08 1.37-.21a4.568 4.568 0 0 0 1.51-.75 4.24 4.24 0 0 0 1.17-1.3 4.36 4.36 0 0 0 .66-2.12z"/></svg>
            </div>
            {/* Trello */}
            <div className="w-6 h-6 " title="Trello">
              <svg viewBox="0 0 24 24" fill="#0052CC"><path d="M21 0H3C1.343 0 0 1.343 0 3v18c0 1.656 1.343 3 3 3h18c1.656 0 3-1.344 3-3V3c0-1.657-1.344-3-3-3zM10.44 18.18c0 .795-.645 1.44-1.44 1.44H4.56c-.795 0-1.44-.645-1.44-1.44V4.56c0-.795.645-1.44 1.44-1.44H9c.795 0 1.44.645 1.44 1.44v13.62zm10.44-6c0 .794-.645 1.44-1.44 1.44H15c-.795 0-1.44-.646-1.44-1.44V4.56c0-.795.645-1.44 1.44-1.44h4.44c.795 0 1.44.645 1.44 1.44v7.62z"/></svg>
            </div>
            {/* Asana */}
            <div className="w-6 h-6 " title="Asana">
              <svg viewBox="0 0 24 24" fill="#F06A6A"><path d="M18.79 12.596c-2.875 0-5.204 2.329-5.204 5.204 0 2.876 2.329 5.205 5.204 5.205S24 20.676 24 17.8c0-2.875-2.33-5.204-5.21-5.204zm-13.58 0c-2.875 0-5.204 2.329-5.204 5.204C.006 20.676 2.335 23 5.21 23s5.204-2.329 5.204-5.205c0-2.875-2.329-5.204-5.204-5.204zM12 1c-2.876 0-5.204 2.33-5.204 5.205 0 2.875 2.328 5.204 5.204 5.204 2.875 0 5.205-2.329 5.205-5.204C17.205 3.33 14.876 1 12 1z"/></svg>
            </div>
            {/* Notion */}
            <div className="w-6 h-6 " title="Notion">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466l1.823 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.84-.046.933-.56.933-1.167V6.354c0-.606-.233-.933-.746-.886l-15.177.887c-.56.046-.747.326-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.448.327s0 .84-1.168.84l-3.22.186c-.094-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.454-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.887.747-.933l3.224-.187z"/></svg>
            </div>
            {/* Airtable */}
            <div className="w-6 h-6 " title="Airtable">
              <svg viewBox="0 0 24 24" fill="#18BFFF"><path d="M11.992.006a1.417 1.417 0 0 0-.617.149L1.125 5.198a1.417 1.417 0 0 0 0 2.536l10.25 5.043c.395.195.84.195 1.235 0l10.25-5.043a1.417 1.417 0 0 0 0-2.536L12.61.155a1.417 1.417 0 0 0-.618-.149zm-10.881 9.03a1.417 1.417 0 0 0-.632.143 1.417 1.417 0 0 0-.473 1.95l4.944 7.69a1.417 1.417 0 0 0 1.667.52l5.346-2.008V11.15l-10.25 5.044c-.395.194-.84.194-1.235 0l-.734-.362a1.417 1.417 0 0 0 .367-4.796zm21.778 0a1.417 1.417 0 0 0-.633.143l-.734.362c-.395.194-.84.194-1.235 0l-6.295-3.1v6.182l5.346 2.008a1.417 1.417 0 0 0 1.667-.52l4.944-7.69a1.417 1.417 0 0 0-1.06-2.385z"/></svg>
            </div>
            {/* Microsoft Teams */}
            <div className="w-6 h-6 " title="Microsoft Teams">
              <svg viewBox="0 0 24 24" fill="#6264A7"><path d="M20.625 8.073h-1.27V6.428c0-.786-.672-1.425-1.503-1.425h-4.2a2.71 2.71 0 0 0 .475-1.534A2.716 2.716 0 0 0 11.417.75 2.716 2.716 0 0 0 8.7 3.469c0 .572.178 1.103.48 1.54a1.513 1.513 0 0 0-1.058 1.42v1.644H6.376A1.877 1.877 0 0 0 4.5 9.95v8.175a1.877 1.877 0 0 0 1.876 1.875h2.998v.563c0 .828.672 1.5 1.5 1.5h8.252c.828 0 1.5-.672 1.5-1.5V10.64a2.572 2.572 0 0 0-2.568-2.567h2.567zM11.417 2.25c.672 0 1.219.547 1.219 1.219s-.547 1.219-1.219 1.219a1.22 1.22 0 0 1-1.219-1.219c0-.672.547-1.219 1.22-1.219zM6 18.125V9.95c0-.206.168-.375.376-.375h5.998v9.675H6.376A.376.376 0 0 1 6 18.125zm12.626 2.437a.001.001 0 0 1 0 .001H10.874V15h7.5v5.563h.252zm0-7.062h-7.252V9.574h4.502c.587 0 1.066.476 1.068 1.062v2.864h1.682z"/></svg>
            </div>
            {/* Discord */}
            <div className="w-6 h-6 " title="Discord">
              <svg viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg>
            </div>
            {/* Dropbox */}
            <div className="w-6 h-6 " title="Dropbox">
              <svg viewBox="0 0 24 24" fill="#0061FF"><path d="M6 1.807L0 5.629l6 3.822 6-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6-3.822-6-3.822-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6 3.822 6-3.822-6-3.822-6 3.822z"/></svg>
            </div>
            {/* Google Drive */}
            <div className="w-6 h-6 " title="Google Drive">
              <svg viewBox="0 0 24 24" fill="#4285F4"><path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.005-.015-1.708-2.996-3.774-6.62L15.764 1.532c-.019-.032-1.673-.047-3.754-.047zm-3.69.047H4.56L.781 7.48l7.555-.015 3.669-5.933zM.025 15.627l3.779 6.574H7.57l3.779-6.574H.025zm8.326 6.574h7.566l3.779-6.574h-7.565l-3.78 6.574z"/></svg>
            </div>
            {/* Stripe */}
            <div className="w-6 h-6 " title="Stripe">
              <svg viewBox="0 0 24 24" fill="#635BFF"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/></svg>
            </div>
            {/* Shopify */}
            <div className="w-6 h-6 " title="Shopify">
              <svg viewBox="0 0 24 24" fill="#7AB55C"><path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zm-1.278-17.58a3.527 3.527 0 0 0-.121-.349s-1.158.37-2.405.37c-.953 0-1.022-.753-1.022-.945 0-1.014.662-2.672 1.803-2.672.342 0 .602.107.806.323.054.072.149.066.191.013.095-.116.148-.187.247-.366-.005-.001-.783-.902-2.008-.902-1.579 0-3.203 1.813-3.203 4.21 0 1.62.626 2.585 1.94 2.585 1.07 0 1.851-.691 2.336-1.461l-.177 1.26h1.696l.917-7.063h-1.685l.685 4.997zm-5.752-.694c0 2.652-.547 4.166-2.233 4.166-.778 0-1.295-.411-1.295-1.434 0-1.79 1.195-2.586 3.528-2.586v-.146zm2.006-1.477c-.859 0-1.87.287-2.626.817-.001-.013-.001-.026-.002-.039-.008-.292.012-.683.012-.683l-2.024.025.03 8.705h2.147c-.055-1.047-.044-2.218-.038-3.371 0-.11-.001-.22 0-.329.547.405 1.273.641 2.073.641 1.996 0 3.716-1.633 3.716-4.767-.001-2.262-1.12-3.999-3.288-3.999z"/></svg>
            </div>
            {/* Twitter/X */}
            <div className="w-6 h-6 " title="X (Twitter)">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
            </div>
            {/* LinkedIn */}
            <div className="w-6 h-6 " title="LinkedIn">
              <svg viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </div>
            {/* GitHub */}
            <div className="w-6 h-6 " title="GitHub">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
            </div>
            {/* Jira */}
            <div className="w-6 h-6 " title="Jira">
              <svg viewBox="0 0 24 24" fill="#0052CC"><path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z"/></svg>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Newly Created Key Alert */}
            {newlyCreatedKey && (
              <Card className="border-green-500/50 bg-green-50 dark:bg-green-950/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <CardTitle className="text-lg text-green-700 dark:text-green-400">
                        API Key Created
                      </CardTitle>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewlyCreatedKey(null)}
                      className="text-green-600 hover:text-green-700"
                    >
                      Dismiss
                    </Button>
                  </div>
                  <CardDescription className="text-green-600 dark:text-green-400">
                    Copy your API key now. You won't be able to see it again!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex space-x-2">
                    <div className="relative flex-1">
                      <Input
                        value={showNewKey ? newlyCreatedKey : '••••••••••••••••••••••••••••••••'}
                        readOnly
                        className="font-mono text-sm bg-white dark:bg-background pr-10"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowNewKey(!showNewKey)}
                      >
                        {showNewKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button onClick={() => copyToClipboard(newlyCreatedKey, 'API key')}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* API Keys Card */}
            <Card className="border-primary/20 shadow-md">
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Key className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">API Keys</CardTitle>
                      <CardDescription>Manage your Zapier API keys</CardDescription>
                    </div>
                  </div>
                  {!showCreateForm && (
                    <Button onClick={() => setShowCreateForm(true)} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Key
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Create Key Form */}
                {showCreateForm && (
                  <form onSubmit={handleCreateKey} className="mb-6 p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Create New API Key</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewKeyName('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="key-name">Key Name</Label>
                        <Input
                          id="key-name"
                          placeholder="e.g., Production Zapier, Test Integration"
                          value={newKeyName}
                          onChange={(e) => setNewKeyName(e.target.value)}
                          disabled={isCreating}
                        />
                        <p className="text-xs text-muted-foreground">
                          Give your key a descriptive name to identify it later
                        </p>
                      </div>
                      <Button type="submit" disabled={isCreating || !newKeyName.trim()}>
                        {isCreating ? 'Creating...' : 'Create API Key'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* API Keys List */}
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8">
                    <Key className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h4 className="font-medium text-muted-foreground mb-2">No API Keys Yet</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create an API key to start using BigSplice with Zapier
                    </p>
                    {!showCreateForm && (
                      <Button onClick={() => setShowCreateForm(true)} variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Your First Key
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {apiKeys.map((key) => (
                      <div
                        key={key.key_id}
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-sm truncate">{key.name}</h4>
                            {key.is_active ? (
                              <span className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                                Active
                              </span>
                            ) : (
                              <span className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">
                                Revoked
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-xs text-muted-foreground">
                            <span>Created: {zapierApiKeyService.formatDate(key.created_at)}</span>
                            {key.last_used_at && (
                              <span>Last used: {zapierApiKeyService.formatDate(key.last_used_at)}</span>
                            )}
                          </div>
                        </div>
                        {key.is_active && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRevokeKey(key.key_id, key.name)}
                            disabled={revokingKeyId === key.key_id}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            {revokingKeyId === key.key_id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zapier Capabilities */}
            <Card>
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Zap className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">What You Can Do with Zapier</CardTitle>
                    <CardDescription>Automate your data workflows</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Upload className="h-4 w-4 text-blue-500" />
                      </div>
                      <h4 className="font-medium">Upload Files</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Automatically upload CSV, Excel, or Parquet files from Google Drive, Dropbox, or URLs
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-purple-500/10 rounded-lg">
                        <Search className="h-4 w-4 text-purple-500" />
                      </div>
                      <h4 className="font-medium">Query Data</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Run SQL queries on your data and use results in other Zapier apps
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-green-500/10 rounded-lg">
                        <Clock className="h-4 w-4 text-green-500" />
                      </div>
                      <h4 className="font-medium">Trigger on New Files</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Start workflows when new files are uploaded to BigSplice
                    </p>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-amber-500/10 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-amber-500" />
                      </div>
                      <h4 className="font-medium">Track Job Status</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Monitor upload and query job status with polling triggers
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pro Tip - Best Practices */}
            <Card className="border-border shadow-md">
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Lightbulb className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      Recommended Workflow
                    </CardTitle>
                    <CardDescription>
                      Get the most out of your data with this two-step approach
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  For the best balance of speed, cost, and freshness:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex-shrink-0 w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Bulk upload your historical data
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Export your existing data as CSV or Excel and upload it directly. This is the fastest and most cost-effective way to bring in large datasets.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex-shrink-0 w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Use Zapier for incremental updates
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Set up a Zap to automatically sync new records as they come in. This keeps your data fresh without re-processing everything.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Why this works:</span> Bulk uploads process data in optimized batches, while Zapier handles real-time triggers efficiently. Together, you get complete historical data plus automatic updates.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Request Native Integration */}
            <Card>
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Request a Native Integration</CardTitle>
                    <CardDescription>
                      Want direct connections without Zapier? Tell us what you need.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {requestSubmitted ? (
                  <div className="text-center py-6">
                    <div className="h-12 w-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h4 className="font-medium text-lg mb-2">Request Received!</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      We've logged your integration request. Our team reviews these regularly to prioritize new connectors.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setRequestSubmitted(false)}
                    >
                      Submit Another Request
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleIntegrationRequest} className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      We're building native integrations for popular platforms. Your input helps us decide what to build next.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="platform-name">Platform or Service</Label>
                      <Input
                        id="platform-name"
                        placeholder="e.g., Shopify, HubSpot, Stripe, Google Analytics"
                        value={integrationRequest.platformName}
                        onChange={(e) =>
                          setIntegrationRequest({
                            ...integrationRequest,
                            platformName: e.target.value,
                          })
                        }
                        disabled={isSubmittingRequest}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="use-case">
                        What would you use it for? <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <Textarea
                        id="use-case"
                        placeholder="e.g., Sync daily sales data for reporting, pull customer records for analysis..."
                        value={integrationRequest.useCase}
                        onChange={(e) =>
                          setIntegrationRequest({
                            ...integrationRequest,
                            useCase: e.target.value,
                          })
                        }
                        disabled={isSubmittingRequest}
                        rows={3}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmittingRequest || !integrationRequest.platformName.trim()}
                      className="w-full"
                    >
                      {isSubmittingRequest ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">
            {/* Setup Instructions */}
            <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800/50 gap-0">
              <CardHeader className="!pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertCircle className="h-5 w-5 text-orange-600 mr-2" />
                  Setup Instructions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">
                      1
                    </span>
                    <span>Create an API key above</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">
                      2
                    </span>
                    <span>Install the BigSplice app in Zapier (invite-only)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">
                      3
                    </span>
                    <span>Connect using your API key when prompted</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">
                      4
                    </span>
                    <span>Start building your Zaps!</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card className="gap-0">
              <CardHeader className="!pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-orange-500" />
                  Integration Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm">API Keys</span>
                    <span className="text-sm font-medium">{apiKeys.filter((k) => k.is_active).length} active</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm">Zapier App</span>
                    <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                      Private Beta
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help & Support */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800/50 gap-0">
              <CardHeader className="!pb-3">
                <CardTitle className="text-lg flex items-center">
                  <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
                  Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="https://zapier.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 rounded-lg hover:bg-background/50 transition-colors group"
                    >
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-1.5 rounded mr-3">
                        <ExternalLink className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block group-hover:text-blue-600 transition-colors">
                          Zapier Website
                        </span>
                        <span className="text-xs text-muted-foreground">Learn about Zapier</span>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="flex items-center p-2 rounded-lg hover:bg-background/50 transition-colors group"
                    >
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-1.5 rounded mr-3">
                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block group-hover:text-blue-600 transition-colors">
                          API Documentation
                        </span>
                        <span className="text-xs text-muted-foreground">Technical reference</span>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="flex items-center p-2 rounded-lg hover:bg-background/50 transition-colors group"
                    >
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-1.5 rounded mr-3">
                        <HelpCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block group-hover:text-blue-600 transition-colors">
                          Contact Support
                        </span>
                        <span className="text-xs text-muted-foreground">Get help with issues</span>
                      </div>
                    </a>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};
